import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import "../../../styles/panel/user/Customers.css";
import http from "../../../api/http";
import { swalError } from "../../../utils/swalAlert";

const PAGE_SIZE_OPTIONS = [7, 14, 21];
const SEARCH_DEBOUNCE_MS = 400;

const toFa = (n) => Number(n || 0).toLocaleString("fa-IR");

export default function Customers({}) {
  const [searchInput, setSearchInput] = useState(""); // مقدار خام ورودی
  const [search, setSearch] = useState(""); // مقدار debounce‌شده که واقعاً به سرور می‌رود
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [page, setPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // ---- debounce کردن جستجو: بعد از توقف تایپ کاربر، مقدار واقعی جستجو آپدیت می‌شود ----
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1); // با تغییر جستجو باید به صفحه‌ی اول برگردیم
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ---- درخواست واقعی به سرور: با هر تغییر در page / pageSize / search ----
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    http
      .get("/orders/user/customers/", {
        params: {
          page,
          page_size: pageSize,
          search,
        },
      })
      .then((res) => {
        if (cancelled) return;
        setCustomers(res.data.results || []);
        setTotalCount(res.data.count || 0);
      })
      .catch(() => {
        if (cancelled) return;
        swalError({ text: "دریافت مشتری‌ها انجام نشد" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // اگر به هر دلیلی صفحه‌ی فعلی از تعداد کل صفحات بیشتر شد (مثلاً بعد از تغییر pageSize)
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages]); // eslint-disable-line react-hooks/exhaustive-deps

  const pageNumbers = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalPages; n++) nums.push(n);
    return nums;
  }, [totalPages]);

  const handleSearch = (value) => {
    setSearchInput(value);
  };

  const handlePageSize = (value) => {
    setPageSize(Number(value));
    setPage(1);
  };

  return (
    <div className="customers-page">
      <div className="customers-header">
        <div>
          <h1>مشتریان</h1>
          <p className="customers-sub">مدیریت و جستجوی مشتریان فروشگاه</p>
        </div>
      </div>

      {/* ---- جستجو ---- */}
      <div className="customers-search">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input
          type="text"
          inputMode="search"
          value={searchInput}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="جستجو بر اساس نام، شماره تماس، استان، شهر، آدرس یا کدپستی"
        />
        {searchInput && (
          <button
            className="customers-search-clear"
            onClick={() => handleSearch("")}
            aria-label="پاک کردن جستجو"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* ---- جدول ---- */}
      <div className="customers-table-card">
        <div className="customers-table-scroll">
          <table className="customers-table">
            <thead>
              <tr>
                <th>#</th>
                <th>نام</th>
                <th>شماره تماس</th>
                <th>استان</th>
                <th>شهر</th>
                <th>آدرس</th>
                <th>کدپستی</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr className="customers-empty-row">
                  <td colSpan={7}>در حال بارگذاری...</td>
                </tr>
              )}
              {!loading && customers.length === 0 && (
                <tr className="customers-empty-row">
                  <td colSpan={7}>
                    {search.trim()
                      ? "مشتری‌ای مطابق با جستجوی شما پیدا نشد."
                      : "هنوز مشتری‌ای ثبت نشده است."}
                  </td>
                </tr>
              )}
              {!loading &&
                customers.map((c, i) => (
                  <tr key={c.id ?? i}>
                    <td data-label="#" className="customers-cell-index">
                      {toFa((page - 1) * pageSize + i + 1)}
                    </td>
                    <td data-label="نام" className="customers-cell-name">
                      {c.name}
                    </td>
                    <td
                      data-label="شماره تماس"
                      className="customers-cell-phone"
                      // dir="ltr"
                    >
                      {c.phone}
                    </td>
                    <td data-label="استان">{c.province}</td>
                    <td data-label="شهر">{c.city}</td>
                    <td data-label="آدرس" className="customers-cell-address">
                      {c.address}
                    </td>
                    <td
                      data-label="کدپستی"
                      className="customers-cell-postal"
                      // dir="ltr"
                    >
                      {c.postal_code}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* ---- صفحه‌بندی ---- */}
        {totalCount > 0 && (
          <div className="customers-pagination">
            <div className="customers-pagination-info-group">
              <span className="customers-pagination-info">
                {toFa(totalCount)} مشتری — صفحه {toFa(page)} از{" "}
                {toFa(totalPages)}
              </span>
              <select
                className="customers-page-size-select"
                value={pageSize}
                onChange={(e) => handlePageSize(e.target.value)}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {toFa(n)}
                  </option>
                ))}
              </select>
            </div>

            <div className="customers-pagination-controls">
              <button
                className="customers-page-btn"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="صفحه قبل"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  className={`customers-page-btn number${n === page ? " active" : ""}`}
                  onClick={() => setPage(n)}
                >
                  {toFa(n)}
                </button>
              ))}
              <button
                className="customers-page-btn"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="صفحه بعد"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
